import type { ApiClient, ReviewCounts } from "./api";
import { useLoaded } from "./useLoaded";

export interface ReviewCountHook {
  /** Both kinds waiting on the owner, split. Null until the first answer. */
  counts: ReviewCounts | null;
  refresh: () => void;
}

/**
 * INS-4 and AUTO-1 — how much is waiting on the owner, held once for the rail's Review-queue
 * badge (UI-3) and the queue navigator's "By type" counts. Its own small poll rather than the
 * two queues' lengths: the badge is on screen everywhere and a queue can be long, so this is a
 * cheap route the badge reads and the review page refreshes as the owner works items off it.
 *
 * A slower poll than the recordings list — the count changes when a source finishes
 * processing or when the owner decides a card, not while they watch — and refreshed on
 * demand so approving an insight decrements the badge without waiting for the next tick.
 *
 * A count that could not be read is left as it was and nothing is reported: a stale badge
 * is better than one that blinks to nothing because one request did not get through.
 */
const Interval = 30_000;

const readCounts = (api: ApiClient): Promise<ReviewCounts | null> => api.getReviewCount();

export function useReview(api: ApiClient): ReviewCountHook {
  const { value, refresh } = useLoaded(api, readCounts, null, "", { poll: Interval });

  return { counts: value, refresh };
}
